import type { ListedAdset } from "./data.ts";
import type { MetaObjectId } from "./ids.ts";
import type { Currency, Minor } from "./money-schemas.ts";
import { minus, pt } from "./number-text.ts";

export const currencies = ["BRL", "USD"] as const;

export function isCurrency(value: unknown): value is Currency {
  return currencies.some((currency) => currency === value);
}

export function isMinor(value: unknown): value is Minor {
  return Number.isSafeInteger(value);
}

export function minorOf(value: number): Minor {
  if (!isMinor(value)) throw new Error(`${value} is not a whole number of minor units`);
  return value;
}

export function minorFromDecimal(text: string): Minor | undefined {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text.trim());
  if (!match) return undefined;
  const [, sign = "", whole = "0", fraction = ""] = match;
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0").slice(0, 2)) + (Number(fraction[2] ?? "0") >= 5 ? 1 : 0);
  const amount = sign === "-" ? -cents : cents;
  return isMinor(amount) ? amount : undefined;
}

export function minorFromAmount(amount: number): Minor | undefined {
  return Number.isFinite(amount) ? minorFromDecimal(String(amount)) : undefined;
}

export function sumMinor(amounts: Iterable<Minor>): Minor {
  let total = 0;
  for (const amount of amounts) total += amount;
  return minorOf(total);
}

export const nearestMinor = (minor: number): Minor => minorOf(Math.round(minor));

export const minorOfUnits = (units: number): Minor => nearestMinor(units * 100);

export const perDay = (amount: Minor, days: number): Minor => minorOf(Math.ceil(amount / days));

export const metaDailyBudget = <B extends number>(minor: B | null | undefined): B | null =>
  (minor === undefined || minor === 0 ? null : minor);

export const lifetimeDaily = (amount: Minor, startAt: string | null, stopAt: string | null): Minor => perDay(amount,
  startAt !== null && stopAt !== null ? Math.max(Math.ceil((Date.parse(stopAt) - Date.parse(startAt)) / 86_400_000), 1) : 1);

type Budgets = Readonly<Pick<ListedAdset, "dailyBudget" | "lifetimeBudget" | "startAt" | "stopAt">>;

export function budgetOf(o: Budgets): Minor | undefined {
  const daily = metaDailyBudget(o.dailyBudget);
  if (daily !== null) return daily;
  return o.lifetimeBudget === null ? undefined : lifetimeDaily(o.lifetimeBudget, o.startAt, o.stopAt);
}

export type Priced = { readonly total: Minor } | { readonly total: null; readonly unpriced: readonly MetaObjectId[] };

export function sumPriced(all: readonly Priced[]): Priced {
  const unpriced = all.flatMap((one) => (one.total === null ? one.unpriced : []));
  const priced = all.flatMap((one) => (one.total === null ? [] : [one.total]));
  return unpriced.length > 0 ? { total: null, unpriced } : { total: sumMinor(priced) };
}

type Listed = Budgets & Readonly<Pick<ListedAdset, "id" | "status">>;

export const pricedOne = (o: Listed): Priced => {
  const daily = budgetOf(o);
  return daily === undefined ? { total: null, unpriced: [o.id] } : { total: daily };
};

export function campaignDaily(campaign: Budgets | undefined, adsets: readonly Listed[]): Priced {
  const own = campaign && budgetOf(campaign);
  return own === undefined ? sumPriced(adsets.filter((a) => a.status === "ACTIVE").map(pricedOne)) : { total: own };
}

export function unpricedAdsets(unpriced: readonly MetaObjectId[]): string {
  const [which, are] = unpriced.length === 1 ? ["ad set", "is"] : ["ad sets", "are"];
  return `${which} ${unpriced.join(", ")} ${are} active with neither a daily nor a lifetime budget`;
}

export const pricedBy = "a daily or lifetime budget on every active ad set, or on the campaign, prices it";

export const movedBy = (amount: Minor, percent: number): Minor => nearestMinor((amount * (100 + percent)) / 100);

export const withTax = (amount: Minor, rate: number): Minor => nearestMinor(amount * (1 + rate));

export const budgetMove = (from: Minor, to: Minor) =>
  ({ perDay: minorOf(to - from), per30Days: minorOf((to - from) * 30), pct: (to - from) / from });

type Centavos = "sempre" | "se-houver" | "abaixo-de-cem" | "nunca";

export interface MoneyFormat {
  readonly centavos?: Centavos;
  readonly sinal?: boolean;
}

export function formatMinor(amount: Minor, currency: Currency, options: MoneyFormat = {}): string {
  const digits = showsCents(amount, options.centavos ?? "sempre") ? 2 : 0;
  const signDisplay = options.sinal === true ? "exceptZero" : "auto";
  const format = pt({ style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits, signDisplay });
  return minus(format.format(amount / 100)).replace(/\s/g, "\u00a0");
}

function showsCents(amount: Minor, centavos: Centavos): boolean {
  if (centavos === "se-houver") return amount % 100 !== 0;
  if (centavos === "abaixo-de-cem") return Math.abs(amount) < 10_000;
  return centavos === "sempre";
}

export function formatUsdCost(usd: number): string {
  return `US$ ${usd.toFixed(2)}`;
}
